import { Link } from "@tanstack/react-router"
import classNames from "classnames"
import { useMemo } from "react"
import { HeaderDisplayState } from "./HeaderDisplayState"
import { HeaderError } from "./HeaderError"
import { Loading } from "./Loading"
import { ProgressBar } from "./ProgressBar"
import { Reading } from "./Reading"
import { useIsLoadingRecentGlucoseData } from "./useIsLoadingRecentGlucoseData"
import styles from "./UserHeader.module.scss"
import {
  NavigationMenuTrigger,
  UserData,
  UserDataSynced,
  UserInitialButton,
} from "src/components"
import {
  useFollowedUsers,
  useRecentReadings,
} from "src/contexts"
import {
  usePumpStatus,
  useSources,
  usePumpStatusRecency,
} from "src/hooks"
import { DataRecency } from "src/types"

export function UserHeader() {
  const { data: sources } = useSources()
  const pumpStatus = usePumpStatus()
  const pumpStatusRecency = usePumpStatusRecency(pumpStatus)

  const {
    currentFollow,
    currentUserId,
  } = useFollowedUsers()

  const {
    readings,
    initialized,
  } = useRecentReadings()

  const recentReading = readings[currentFollow.following_account_id]
  const isLoading = useIsLoadingRecentGlucoseData()
  const syncedAt = pumpStatus?.last_pump_event_at ?? recentReading?.startedAt ?? null

  const displayState = useMemo(
    () => {
      const source = sources?.[0]

      if (currentFollow.is_self && !source) {
        return HeaderDisplayState.NoDataSource
      }

      if (
        pumpStatus !== undefined &&
        pumpStatusRecency !== DataRecency.Stale &&
        pumpStatus.sensor_expiration === null
      ) {
        return HeaderDisplayState.NoCGMSession
      }

      if (recentReading) {
        return HeaderDisplayState.HasData
      }

      if (isLoading || !initialized) {
        return HeaderDisplayState.LoadingData
      }

      if (pumpStatus !== undefined && pumpStatusRecency === DataRecency.Stale) {
        return HeaderDisplayState.NoPumpOrCGMData
      }

      return HeaderDisplayState.NoCGMData
    },
    [
      currentFollow,
      sources,
      recentReading,
      isLoading,
      initialized,
      pumpStatusRecency,
      pumpStatus,
    ],
  )

  const displayContent = useMemo(
    () => {
      switch (displayState) {
        case HeaderDisplayState.HasData:
          return (
            <Reading reading={recentReading} />
          )
        case HeaderDisplayState.LoadingData:
          return <Loading />
        case HeaderDisplayState.NoDataSource:
        case HeaderDisplayState.NoPumpOrCGMData:
        case HeaderDisplayState.NoCGMData:
        case HeaderDisplayState.NoCGMSession:
          return <HeaderError displayState={displayState} />
        default:
          return <HeaderError displayState={HeaderDisplayState.NoCGMData} />
      }
    },
    [
      displayState,
      recentReading,
    ],
  )

  const hideSyncedAt = displayState === HeaderDisplayState.LoadingData ||
    displayState === HeaderDisplayState.NoDataSource

  const getLinkTo = useMemo(
    () => {
      if (!currentFollow.is_self) {
        return "/nightstand/$accountId"
      }

      return displayState === HeaderDisplayState.NoDataSource
        ? "/account/data_source"
        : "/nightstand"
    },
    [
      currentFollow.is_self,
      displayState,
    ],
  )

  const getLinkParams = useMemo(
    () => {
      if (currentFollow.is_self) {
        return {}
      }

      return { accountId: currentUserId }
    },
    [
      currentFollow.is_self,
      currentUserId,
    ],
  )

  return (
    <div className={styles.container}>
      <ProgressBar />
      <div className={styles.content}>
        <Link
          to={getLinkTo}
          params={getLinkParams}
          className={styles.link}
        >
          {displayContent}
        </Link>
        <UserData
          pumpStatus={pumpStatus}
          className={styles.userData}
        />
        <UserDataSynced
          className={classNames(
            styles.syncedAt,
            hideSyncedAt && styles.hidden,
          )}
          syncedAt={syncedAt}
        />
      </div>
      <NavigationMenuTrigger asChild>
        <UserInitialButton
          className={styles.userInitial}
          user="follow"
        />
      </NavigationMenuTrigger>
    </div>
  )
}
